import { Fragment, FunctionComponent } from 'react'
import styled from 'styled-components'

import Spacer from 'components/Spacer'

import { useFieldSchema } from 'services/swr/useFieldSchema'

import {
  filterVisibleFieldsForScreen,
  groupFields,
  showFieldsWithValues,
  showPositiveFields,
  showValue,
} from 'utils/listingHelpers'

import * as T from 'types'

const groupOrder: { [key: string]: number | undefined } = {
  'Basic Home Details': 1,
  'Interior Details': 2,
  'Structure & Exterior': 3,
  'Lot Details': 4,
  'Special Features & Designations': 5,
  'Community Details': 6,
  'Utilities & Disclosures': 7,
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
`

const SectionHeader = styled.div`
  padding: 0 20px;
  height: 55px;
  display: flex;
  align-items: center;
  background: ${props => props.theme.colors.grey};

  font-style: normal;
  font-weight: 800;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 1.28571px;
  color: ${props => props.theme.colors.black};
`

const SectionContent = styled.div`
  padding: 24px 20px 20px;
  display: flex;
  flex-direction: column;
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    flex-direction: row;
  }
`

const SectionColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`

const ListHeader = styled.div`
  font-style: normal;
  font-weight: 800;
  font-size: 16px;
  line-height: 24px;
`

const UnorderedList = styled.ul`
  margin: 0;
  padding-inline-start: 15px;
`

const ListItem = styled.li`
  font-style: normal;
  font-size: 16px;
  line-height: 24px;
`

const renderGroupedFields = (fieldGroup: T.IMlsField[], idx: number, last: number) => (
  <Fragment key={`${fieldGroup[0].listingFlowSubStep}-${idx}`}>
    <ListHeader>{fieldGroup[0].listingFlowSubStep}</ListHeader>
    <UnorderedList
      data-testid={`${fieldGroup[0].listingFlowStep}-${fieldGroup[0].listingFlowSubStep}`}
    >
      {fieldGroup.map(field => {
        const fieldValue = showValue(field)
        return (
          <ListItem key={`${field.publicLabel}-${field.fieldId?._id || field._id}`}>
            {`${field.publicLabel || field.label || field.name}: ${fieldValue}`}
          </ListItem>
        )
      })}
    </UnorderedList>
    {idx !== last && <Spacer size={24} />}
  </Fragment>
)

interface IProps {
  listing: T.IListing
}

const ListingDetails: FunctionComponent<IProps> = ({ listing }) => {
  const { fieldSchema } = useFieldSchema(listing)
  const fieldsInSchema: T.IMlsField[] = fieldSchema ? fieldSchema.fields : []
  const visibleFields = filterVisibleFieldsForScreen(listing, fieldsInSchema, '', true)
  const fields: T.IMlsField[] = showPositiveFields(showFieldsWithValues(listing, visibleFields))

  const groupedFields = groupFields(fields, 'listingFlowStep')
    .map(stepGroupedFields => groupFields(stepGroupedFields, 'listingFlowSubStep'))
    .sort((a, b) => {
      const aOrder = groupOrder[a[0][0].listingFlowStep || ''] || 10000
      const bOrder = groupOrder[b[0][0].listingFlowStep || ''] || 10000
      return aOrder - bOrder
    })

  return (
    <Container>
      {listing.description && (
        <>
          <SectionHeader>Description</SectionHeader>
          <SectionContent>{listing.description}</SectionContent>
        </>
      )}
      {groupedFields
        && !!groupedFields.length
        && groupedFields.map((stepFieldGroup, idx) => {
          const fieldGroup = [...stepFieldGroup]
            .map(step =>
              step.filter(field => {
                // explicit carve outs for sections we should not display
                if (field.listingFlowStep === 'Description') return false

                if (field.label === 'Property Type') return false

                if (!showValue(field) || showValue(field) === 'N/A') return false

                return true
              }),
            )
            .filter(step => !!step.length)

          const half = Math.ceil(fieldGroup.length / 2)
          const firstHalfFields: T.IMlsField[][] = fieldGroup.splice(0, half)
          const secondHalfFields: T.IMlsField[][] = fieldGroup.splice(-half)

          if (!firstHalfFields[0] || !firstHalfFields[0][0]) return null

          return (
            /* eslint-disable-next-line  react/no-array-index-key */
            <Fragment key={`${firstHalfFields[0][0].listingFlowStep}-${idx}`}>
              <SectionHeader>{firstHalfFields[0][0].listingFlowStep}</SectionHeader>
              <SectionContent>
                <SectionColumn>
                  {firstHalfFields.map((fieldGroups, index) =>
                    renderGroupedFields(fieldGroups, index, firstHalfFields.length - 1),
                  )}
                </SectionColumn>
                {!!secondHalfFields.length && <Spacer size={24} />}
                <SectionColumn>
                  {secondHalfFields.map((fieldGroups, index) =>
                    renderGroupedFields(fieldGroups, index, secondHalfFields.length - 1),
                  )}
                </SectionColumn>
              </SectionContent>
            </Fragment>
          )
        })}
    </Container>
  )
}

export default ListingDetails
